<template>
  <div>
    <div v-if="messageList!=null && messageList.length>0" class="chat-container background-container">
      <div class="chat-messages" ref="chatMessages">
        <div v-if="messageList!=null">
          <div :class="{'hostaway-message': message.isIncoming, 'message-user': !message.isIncoming}"
               v-for="(message, index) in messageList" :key="index">
            <template v-if="!message.isIncoming">
              <div class="message-card-user">
                <span class="message-text">{{ removeHtmlTags(message.body) }}</span>
                <p class="message-date">{{ $moment(message.date).format('DD/MM/YYYY HH:mm:ss') }}</p>
              </div>
            </template>
            <template v-else>
              <div class="message-card-client">
                <span class="message-text">{{ removeHtmlTags(message.body) }}</span>
                <p class="message-date">{{ $moment(message.date).format('DD/MM/YYYY HH:mm:ss') }}</p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="mt-0">
      <div class="text-center p-5">
        <div class="no-conversation">
          <i class="fas fa-stop mx-1 mt-4" style="color: #e75634"></i>{{ msg("No conversation yet") }}
        </div>
      </div>
    </div>

    <!-- Input / Actions -->
    <div class="chat-input mt-2">
      <b-row class="align-items-center">
        <b-col cols="12" md="10" class="mb-2 mb-md-0">
          <b-input
              :disabled="isInputDisabled || isTemplate"
              type="text"
              v-model="newMessage.body"
              placeholder="Type a message..."
          ></b-input>
        </b-col>

        <b-col cols="12" md="2" class="d-flex align-items-center justify-content-md-end">
          <div class="d-flex align-items-center w-100 justify-content-between justify-content-md-end">
            <!-- Template path -->
            <template v-if="isTemplate">
              <b-button
                  variant="primary"
                  size="md"
                  class="mr-2 flex-grow-1 flex-md-grow-0"
                  @click="openTemplateModal"
              >
                Template
              </b-button>

              <b-button
                  variant="link"
                  class="p-0 ml-2"
                  v-b-popover.click="'To message a client who hasn’t replied in the last 24 hours, WhatsApp requires using a pre-approved template first. If the client responds, you may continue chatting freely within a 24-hour window.'"
                  title="WhatsApp Business Policy"
              >
                <i class="fas fa-info-circle text-danger" style="font-size: 1.2rem;"></i>
              </b-button>
            </template>

            <!-- Free chat path -->
            <template v-else>
              <b-button
                  variant="primary"
                  size="md"
                  class="flex-grow-1 flex-md-grow-0"
                  :disabled="!newMessage.body || !clientId"
                  @click="sendMessage(false)"
              >
                Send
              </b-button>
            </template>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Template Picker Modal -->
    <b-modal
        id="template-picker"
        ref="templateModal"
        title="Choose a WhatsApp Template"
        size="lg"
        hide-footer
        body-class="p-0"
        :no-close-on-esc="loadingTemplates"
        :no-close-on-backdrop="loadingTemplates"
    >
      <div class="p-3">
        <div class="d-flex flex-column flex-md-row align-items-md-center mb-3 ">

          <div class="d-flex align-items-center ml-md-2">
            <b-form-select
                v-model="templateLang"
                :options="languageOptions"
                class="mr-2"
            />
            <b-form-select
                v-model="templateCategory"
                :options="categoryOptions"
            />
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loadingTemplates" class="text-center py-4">
          <b-spinner small class="mr-2"></b-spinner>
          Loading templates…
        </div>

        <!-- Empty -->
        <div v-else-if="filteredTemplates.length === 0" class="text-center py-4 text-muted">
          No templates found.
        </div>

        <!-- Grid -->
        <div v-else class="template-grid">
          <div
              v-for="(tpl, i) in filteredTemplates"
              :key="tpl.id || tpl.name + i"
              class="template-card"
              @click="selectTemplate(tpl)"
          >
            <div class="d-flex align-items-center justify-content-between">
              <div class="font-weight-bold text-truncate">{{ tpl.name }}</div>
              <b-badge :variant="tpl.status === 'APPROVED' ? 'success' : 'secondary'">
                {{ tpl.status || 'UNKNOWN' }}
              </b-badge>
            </div>

            <div class="mt-2 small text-muted d-flex align-items-center flex-wrap">
              <span class="mr-2">
                <i class="fas fa-language mr-1"></i>{{ tpl.language || 'n/a' }}
              </span>
              <span class="mr-2">
                <i class="fas fa-folder-open mr-1"></i>{{ tpl.category || 'n/a' }}
              </span>
            </div>

            <div class="template-body mt-3">
              {{ extractBodyText(tpl) }}
            </div>

            <!-- NEW: dedicate an actions row that's pushed to the bottom -->
            <div class="template-actions">
              <b-button
                  variant="outline-primary"
                  size="sm"
                  @click.stop="selectTemplate(tpl)"
              >
                {{msg('Use Template')}}
              </b-button>
            </div>

          </div>
        </div>

        <div class="mt-3 d-flex justify-content-end">
          <b-button variant="secondary" class="mr-2" @click="closeTemplateModal" :disabled="loadingTemplates">Close
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {mapActions} from "vuex";

export default {
  name: "WhatsappChatComponent",
  props: ['clientId', 'reservationId'],
  watch: {
    clientId: {
      handler() {
        this.refresh();
      },
      immediate: true
    },
    whatsapp: {
      handler() {
        this.refresh();
      },
      immediate: true
    }
  },
  data: () => ({
    loading: true,
    refreshIntervalId: null,
    ready: true,
    newMessage: {body: '', reservationId: ''},
    whatsappMessage: {},
    messageList: [],

    // Templates UI
    templates: [],
    loadingTemplates: false,
    templateSearch: '',
    templateLang: 'all',
    templateCategory: 'all',
  }),
  mounted() {
    this.startRefresh();
  },
  beforeDestroy() {
    this.stopRefresh();
  },
  computed: {
    isInputDisabled() {
      return this.messageList == null || this.messageList.length < 1;
    },
    isTemplate() {
      if (this.messageList == null || this.messageList.length < 1) return true;
      const firstItemDate = new Date(this.messageList[0].date);
      const twentyFourHoursAgo = new Date().getTime() - 24 * 60 * 60 * 1000;
      return firstItemDate.getTime() < twentyFourHoursAgo;
    },
    languageOptions() {
      const langs = Array.from(new Set((this.templates || []).map(t => t.language).filter(Boolean)));
      return [{value: 'all', text: 'All languages'}, ...langs.map(l => ({value: l, text: l}))];
    },
    categoryOptions() {
      const cats = Array.from(new Set((this.templates || []).map(t => t.category).filter(Boolean)));
      return [{value: 'all', text: 'All categories'}, ...cats.map(c => ({value: c, text: c}))];
    },
    filteredTemplates() {
      const q = (this.templateSearch || '').toLowerCase();
      return (this.templates || [])
          .filter(t => this.templateLang === 'all' || t.language === this.templateLang)
          .filter(t => this.templateCategory === 'all' || t.category === this.templateCategory)
          .filter(t => {
            const hay = [
              t.name || '',
              this.extractBodyText(t) || '',
              t.category || '',
              t.language || ''
            ].join(' ').toLowerCase();
            return hay.includes(q);
          });
    }
  },
  methods: {
    ...mapActions('whatsapp', [
      'getMessagesByClientId',
      'sendWhatsappMessage',
      // Add this action on your store; it should return WhatsAppTemplatesResponse
      'getWhatsappTemplates'
    ]),

    removeHtmlTags(text) {
      const div = document.createElement('div');
      div.innerHTML = text;
      return div.textContent || div.innerText || '';
    },

    startRefresh() {
      this.refreshIntervalId = setInterval(() => {
        this.refresh();
      }, 10000);
    },
    stopRefresh() {
      clearInterval(this.refreshIntervalId);
    },

    getMessageClasses(message, isCrm = false) {
      return {
        'message-container': true,
        'hostaway-message': !isCrm && message.isIncoming,
        'crm-message': isCrm || message.source === 'crm'
      };
    },

    // Sending
    sendMessage(isTemplate) {
      if (isTemplate) {
        // now handled by modal -> choose a template first
        this.openTemplateModal();
        return;
      }
      this.ready = false;
      const newMessage = {
        body: this.newMessage.body,
        clientId: this.clientId
      };
      this.sendWhatsappMessage({newMessage, template: false})
          .then(this.onMessagesRetrievedAfterSend);
    },

    // Template modal controls
    openTemplateModal() {
      try {
        let $this=this;
        this.loadingTemplates = true;
        // Lazy-load templates each time (or cache as you prefer)
        this.getWhatsappTemplates({clientId:this.clientId}).then(
            resp => $this.templates = resp["response"]
      )
        ;
      } catch (e) {
        this.templates = [];
        // Optional: toast error
      } finally {
        this.loadingTemplates = false;
        this.$nextTick(() => this.$refs.templateModal && this.$refs.templateModal.show());
      }
    },
    closeTemplateModal() {
      this.$refs.templateModal && this.$refs.templateModal.hide();
    },

    extractBodyText(tpl) {
      // WhatsApp template example: components: [{type:'HEADER'...}, {type:'BODY', text:'...'}]
      if (!tpl || !tpl.components) return '';
      const body = tpl.components.find(c => c.type === 'BODY');
      return body && body.text ? body.text : '';
    },

    async selectTemplate(tpl) {
      this.ready = false;

      // Use the template text as the body we send
      const body = this.extractBodyText(tpl) || '';

      const waReq = {
        body,                          // <-- send body even for template
        clientId: this.clientId,
        isTemplate: true,
        templateName: tpl.name,
        templateLanguage: tpl.language || 'en',
      };

      try {
        await this.sendWhatsappMessage({ newMessage: waReq, template: true });
        this.onMessagesRetrievedAfterSend({ messages: this.whatsappMessage });
        this.closeTemplateModal();
      } catch (e) {
        console.error('Failed to send template:', e);
      } finally {
        this.ready = true;
      }
    }
    ,

    // Fetch & refresh
    refresh() {
      if (!this.clientId) return;
      this.getMessagesByClientId(this.clientId).then(this.onMessagesRetrieved);
    },
    onMessagesRetrieved(data) {
      this.whatsappMessage = data["messages"];
      if (this.whatsappMessage == null || this.whatsappMessage.result == null) {
        this.messageList = [];
        return;
      }
      this.messageList = [...this.whatsappMessage.result].reverse();
    },
    onMessagesRetrievedAfterSend(data) {
      this.whatsappMessage = data["messages"];
      if (this.whatsappMessage == null || this.whatsappMessage.result == null) {
        this.messageList = [];
        return;
      }
      this.messageList = [...this.whatsappMessage.result].reverse();
      this.newMessage = {body: '', reservationId: ''};
      this.ready = true;
    },

    // Optional date util
    formatDate(date) {
      const options = {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
      };
      const formattedDate = new Date(date).toLocaleString('de-AT', options);
      return formattedDate.replace(',', '');
    }
  }
}
</script>

<style scoped>
/* Chat area (kept from your original) */
.chat-messages {
  display: flex;
  flex-direction: column;
  max-height: 400px;
  overflow-y: auto;
  padding: 10px;
}

.message-card-user {
  background-color: #075E54;
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 8px;
  color: white;
  align-self: flex-end;
  max-width: 25%;
  margin-left: auto;
}

.message-card-client {
  background-color: #FEA47F;
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 8px;
  color: #000000;
  align-self: flex-start;
  max-width: 25%;
}

.hostaway-message {
  align-self: flex-start;
}

.message-user {
  align-self: flex-end;
}

.message-text {
  font-weight: bold;
  font-size: 14px;
}

.message-date {
  font-size: 9px;
}

.background-container {
  display: flex;
  flex-direction: column;
  background-size: cover;
  background-position: center;
  background-color: rgba(255, 255, 255, 0);
}

.no-conversation {
  border: 2px dashed #e75634;
  padding: 0px 20px 20px;
  margin-top: 0px;
  display: inline-block;
  border-radius: 10px;
}

/* Template picker — responsive, mobile friendly */
.template-grid {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 12px;
}

@media (min-width: 576px) {
  .template-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 992px) {
  .template-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.template-card {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.1s ease;
}

.template-card:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.template-body {
  font-size: 0.95rem;
  line-height: 1.25rem;
  white-space: pre-wrap;
}

/* Make action row tidy on mobile */
.chat-input .btn {
  min-width: 100px;
}

@media (max-width: 767.98px) {
  .message-card-user, .message-card-client {
    max-width: 85%;
  }
}

/* Make each card a flex column so we can push the actions row down */
.template-card {
  display: flex;
  flex-direction: column;
  height: 100%;              /* ensure it fills the grid cell */
}

/* Actions bar stays at the bottom-right */
.template-actions {
  margin-top: auto;          /* pushes this row to the bottom */
  display: flex;
  justify-content: flex-end; /* right-align the button */
  padding-top: 8px;          /* small breathing room above */
}

/* Optional: ensure grid items stretch so height:100% on card works well */
.template-grid {
  align-items: stretch;
}

</style>
